import { motion } from 'motion/react';
import { FaxTypes } from '@frontend/api-fax';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Avatar, ListRow } from '@frontend/design-system';
import { BulkActionCheckbox } from './bulk-action-checkbox';
import { ListItemStatus } from './list-item-status';

const LEAD_CONTAINER = {
  LARGE: 52,
  SMALL: 42,
} as const;

type FaxInboxListItemLeadProps = {
  isBulkSelect: boolean;
  isCheckboxSelected: boolean;
  personName: string;
  contactId?: string;
  locationId?: string;
  setCheckboxSelected?: (value: boolean) => void;
  profileMedia?: string;
  isInbound: boolean;
  status?: FaxTypes.FaxStatus;
};

export const FaxInboxListItemLead = ({
  isBulkSelect,
  personName,
  contactId,
  locationId,
  isCheckboxSelected,
  setCheckboxSelected,
  profileMedia,
  isInbound,
  status,
}: FaxInboxListItemLeadProps) => {
  const showStatus =
    status !== undefined &&
    status !== FaxTypes.FaxStatus.UNREAD &&
    status !== FaxTypes.FaxStatus.READ &&
    (status === FaxTypes.FaxStatus.DRAFT ||
      !isInbound ||
      status === FaxTypes.FaxStatus.ARCHIVED ||
      status === FaxTypes.FaxStatus.BLOCKED);
  const { setShow } = useSlidePanelShallowStore('setShow');
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

  const openContactForm = () => {
    if (contactId) {
      setShow(true, 'faxContactForm', {
        contactId,
        locationId: locationId ?? '',
        action: FaxTypes.ContactAction.EDIT,
      });
    } else {
      setShow(true, 'faxContactForm', {
        action: FaxTypes.ContactAction.ADD,
        locationId: locationId ?? '',
      });
    }
  };

  return (
    <ListRow.Lead>
      <div
        css={{
          display: 'flex',
          overflow: 'hidden',
          alignItems: 'center',
          width: isMobile ? LEAD_CONTAINER.SMALL : LEAD_CONTAINER.LARGE,
          padding: theme.spacing(0.25),
        }}
      >
        <motion.div
          initial={false}
          animate={{ x: isBulkSelect ? -LEAD_CONTAINER.LARGE : 0 }}
          transition={{ ease: 'easeInOut' }}
        >
          <div css={{ position: 'relative', display: 'inline-flex' }}>
            {profileMedia ? (
              <img
                css={{
                  height: isMobile ? theme.spacing(5) : theme.spacing(6),
                  width: isMobile ? theme.spacing(5) : theme.spacing(6),
                  minWidth: isMobile ? theme.spacing(5) : theme.spacing(6),
                  borderRadius: theme.borderRadius.full,
                }}
                src={profileMedia}
              />
            ) : (
              <Avatar
                name={personName.trim()}
                onClick={(e) => {
                  e?.stopPropagation();
                  openContactForm();
                }}
                css={{
                  height: isMobile ? theme.spacing(5) : theme.spacing(6),
                  width: isMobile ? theme.spacing(5) : theme.spacing(6),
                  minWidth: isMobile ? theme.spacing(5) : theme.spacing(6),
                }}
              />
            )}
            {showStatus && (
              <ListItemStatus
                status={status}
                size={14}
                css={{
                  position: 'absolute',
                  bottom: -2,
                  left: -2,
                  boxShadow: `0 0 0 1.5px ${theme.colors.white}`,
                }}
              />
            )}
          </div>
        </motion.div>
        <motion.div
          initial={false}
          animate={{
            x: isBulkSelect ? -LEAD_CONTAINER.SMALL : theme.spacing(1),
          }}
          transition={{ ease: 'easeInOut' }}
        >
          <BulkActionCheckbox
            isCheckboxSelected={isCheckboxSelected}
            onSelect={(value) => {
              if (typeof setCheckboxSelected === 'function') {
                setCheckboxSelected(value);
              }
            }}
            bulkSelectionIsActive={isBulkSelect}
            isInbound={isInbound}
          />
        </motion.div>
      </div>
    </ListRow.Lead>
  );
};
